import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Briefcase, KeyRound, Loader, Sparkles, ShieldCheck, Crown } from "lucide-react";
import Navbar from "../components/Navbar";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const RECRUITER_NAVY = "#0A2540";
const RECRUITER_GREEN = "#047857";
const RECRUITER_GREEN_DEEP = "#064E3B";
const RECRUITER_GOLD = "#D4AF37";
// Aliases preserved so we can repaint by retargeting two consts.
const RECRUITER_PURPLE = RECRUITER_NAVY;
const RECRUITER_DEEP = RECRUITER_GREEN;

export default function RecruiterSignup() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [agencyName, setAgencyName] = useState("");
  const [betaCode, setBetaCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // If already a recruiter, slide over to the dashboard.
  useEffect(() => {
    if (user?.is_recruiter) navigate("/recruiter", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!agencyName || !betaCode) {
      setError("Agency name and beta code are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/recruiter/signup", { agency_name: agencyName, beta_code: betaCode });
      if (refresh) await refresh();
      navigate("/recruiter");
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || "Couldn't sign up.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen rm-paper" data-testid="recruiter-signup-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-2"
               style={{ color: RECRUITER_PURPLE, fontWeight: 700 }}>
            · Resmo Pro · Talent Cloud
          </div>
          <h1 className="font-display uppercase leading-none mb-3"
              style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)", color: "#0F1B2D", letterSpacing: "-0.02em" }}
              data-testid="recruiter-signup-title">
            The recruiter side <br />
            of <span style={{
              background: `linear-gradient(135deg, ${RECRUITER_PURPLE} 0%, ${RECRUITER_DEEP} 100%)`,
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              backgroundClip: "text", color: RECRUITER_PURPLE,
            }}>Resmo</span>.
          </h1>
          <p className="text-sm md:text-base max-w-2xl mx-auto" style={{ color: "#475569" }}>
            Built for agency recruiters, career coaches, and resume writers.
            The same toolkit your clients use — applied at scale, with the
            Verified-by-Resmo Honesty Seal carried into every submission.
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-6 md:gap-8">
          {/* Left — value props */}
          <div className="md:col-span-3 space-y-3" data-testid="recruiter-value-props">
            {[
              {
                Icon: Briefcase,
                title: "Manage every client in one place",
                sub: "Each client gets their own resume / skill assessment / tailored applications, all owned by you. Add manually for prep work, or send an email invite when ready to hand off.",
              },
              {
                Icon: Sparkles,
                title: "Tailor on their behalf",
                sub: "Run the full Resmo toolkit — Tailor, Strategy Brief, ATS scoring, Honesty Check — without your client ever logging in. Hand them a polished kit, branded with your agency.",
              },
              {
                Icon: ShieldCheck,
                title: "Submit with the Verified seal",
                sub: "Every tailored resume carries Resmo's Honesty Check audit. Clients on competing platforms ship resumes their hiring managers don't trust — yours don't.",
              },
              {
                Icon: Crown,
                title: "Talent Cloud · Coming Q3",
                sub: "Search opted-in candidates by skill, tier, and salary band. Permission-based outreach — no LinkedIn-style spam. Each candidate has a Verified seal you can show your client.",
                muted: true,
              },
            ].map((v, i) => {
              const Ic = v.Icon;
              return (
                <div key={i}
                     className="rm-card-static p-4 flex items-start gap-3"
                     style={{
                       background: v.muted ? "#F8FAFC" : "white",
                       borderColor: "#0F1B2D",
                       boxShadow: `3px 3px 0 0 ${v.muted ? "#94A3B8" : RECRUITER_PURPLE}`,
                       opacity: v.muted ? 0.9 : 1,
                     }}
                     data-testid={`recruiter-value-${i + 1}`}>
                  <div style={{ color: v.muted ? "#475569" : RECRUITER_PURPLE, flexShrink: 0 }}>
                    <Ic size={18} />
                  </div>
                  <div>
                    <div className="font-display uppercase text-sm leading-tight"
                         style={{ color: "#0F1B2D", letterSpacing: "-0.01em" }}>
                      {v.title}
                    </div>
                    <p className="text-[12.5px] leading-snug mt-1" style={{ color: "#475569" }}>
                      {v.sub}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right — signup form */}
          <div className="md:col-span-2">
            <form onSubmit={submit}
                  className="rm-card-static p-5 md:p-6"
                  style={{
                    background: "white",
                    borderColor: "#0F1B2D",
                    boxShadow: `5px 5px 0 0 ${RECRUITER_PURPLE}`,
                  }}
                  data-testid="recruiter-signup-form">
              <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-2"
                   style={{ color: RECRUITER_DEEP, fontWeight: 700 }}>
                · Beta access · Invite only
              </div>
              <h2 className="font-display uppercase text-xl leading-tight mb-3"
                  style={{ color: "#0F1B2D", letterSpacing: "-0.02em" }}>
                Open the recruiter desk.
              </h2>
              <p className="text-[12.5px] mb-4" style={{ color: "#475569" }}>
                Resmo Pro is invite-only during beta. Email{" "}
                <a href="mailto:hello@resmo.app?subject=Resmo%20Pro%20beta%20access"
                   className="underline" style={{ color: RECRUITER_PURPLE }}>
                  hello@resmo.app
                </a>{" "}
                for a code.
              </p>

              <div className="space-y-3">
                <div>
                  <label className="rm-label text-[11px]">
                    <Briefcase size={11} style={{ display: "inline", marginRight: 4 }} />
                    Agency / Practice name
                  </label>
                  <input value={agencyName}
                         onChange={(e) => setAgencyName(e.target.value)}
                         className="rm-input w-full"
                         placeholder="Acme Talent · or 'Solo coach' if you fly alone"
                         data-testid="recruiter-signup-agency"
                         required />
                </div>
                <div>
                  <label className="rm-label text-[11px]">
                    <KeyRound size={11} style={{ display: "inline", marginRight: 4 }} />
                    Beta access code
                  </label>
                  <input value={betaCode}
                         onChange={(e) => setBetaCode(e.target.value)}
                         className="rm-input w-full"
                         placeholder="RESMO-PRO-…"
                         data-testid="recruiter-signup-code"
                         required />
                </div>
              </div>

              {error && (
                <div className="rm-card-static p-2 mt-3 text-xs font-mono-rm"
                     style={{ background: "#FEE2E2", borderColor: "#B91C1C", color: "#991B1B" }}
                     data-testid="recruiter-signup-error">
                  · {error}
                </div>
              )}

              <button type="submit"
                      disabled={busy}
                      className="rm-btn text-sm px-4 py-2.5 mt-4 w-full justify-center"
                      style={{
                        background: RECRUITER_PURPLE,
                        color: "white",
                        border: `2px solid ${RECRUITER_DEEP}`,
                        boxShadow: `4px 4px 0 0 ${RECRUITER_DEEP}`,
                        fontWeight: 700,
                      }}
                      data-testid="recruiter-signup-submit">
                {busy ? <Loader size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                {busy ? "Activating…" : "Open my desk"}
              </button>

              <p className="text-[10px] font-mono-rm uppercase tracking-widest mt-3 text-center"
                 style={{ color: "#94A3B8" }}>
                · Free during beta · Pro pricing rolls out Q2
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
